import {
  Box,
  Divider,
  Grid,
  IconButton,
  useTheme,
  Tooltip,
  CircularProgress
} from '@material-ui/core';
import { CSSProperties, memo, useMemo, useState } from 'react';
import { useRfqId } from 'pages/auto-rfq/context/rfq-id';

import {
  AIProductMatchBadge,
  SButton,
  SETVIImage,
  SHighlightText,
  SText
} from '@setvi/shared/components';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import { useDialog } from '@setvi/shared/providers';
import {
  AnalyticsModules,
  useAnalytics,
  useSubscribedMutation
} from '@setvi/shared/hooks';
import {
  AutoRFQQueryKey,
  createInternalNoteMutation,
  deleteCustomerNoteMutation,
  deleteInternalNoteMutation,
  saveCustomerNoteMutation,
  updateInternalNoteMutation
} from 'Services';
import { Product } from '@setvi/shared/interfaces';
import {
  getProductStatus,
  ImagePreview,
  ProductLabel,
  ProductStatusBadge
} from 'componentsV2';

import ManualEntry, {
  ManualProps
} from 'pages/auto-rfq/pages/details/components/manual-entry';
import { Stock } from 'pages/products/components/stock';
import { RfqMatchOverride, RFQProducts } from 'pages/auto-rfq/interfaces';
import { useRFQStore } from 'pages/auto-rfq/store';
import { AdditionalContent } from 'pages/products/components/products-list/components';
import { getStockInfo } from 'pages/products/components/products-list/useProductColumns/contents/stock-info';
import {
  BaseDialog,
  PrivateLabels
} from 'pages/auto-rfq/pages/details/components';
import { Tabs } from 'pages/auto-rfq/pages/details/components/base-dialog';

import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { SlashedFileIconV2 } from '@setvi/shared/components/sicons';
import { useRfqConfigStore } from 'pages/auto-rfq/store/rfqConfig';
import { useCatalogConfig } from 'pages/products/hooks/useCatalogConfig';
import { RowPresence } from 'pages/auto-rfq/pages/details/components/presence/row-presence';
import { useRfqRealtimeBroadcast } from 'pages/auto-rfq/pages/details/hook/realtime';
import type { SpaceMember } from '@ably/spaces';
import {
  RfqAlternate,
  RfqAlternateSlot,
  RfqPosition
} from 'Services/Query/Auto-RFQ/Types';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@material-ui/icons/KeyboardArrowUpRounded';
import RestoreRoundedIcon from '@material-ui/icons/RestoreRounded';
import { Container, FlexRow, iconStyle, RemovedBadge } from './styled';
import {
  Competitor,
  ExternalChip,
  MatchIndicator,
  NoMatch,
  PricingDisplay
} from './components';
import { AlternateSlot } from './components/alternate-slot';
import { alternatesAnalytics } from '../../utils/alternates-analytics';
import NotesDialog from './components/notes-dialog';
import { AnchorBtn } from '../private-labels/styled';

const NOTES_DIALOG = 'rfq-notes-dialog';

// Top-match reorder / action controls — kept visually in sync with the
// alternate rows (see alternate-slot): ↑ ↓ as small outlined squares, and a
// labeled "Reset" SButton.
const reorderBtnStyle: CSSProperties = {
  border: '1px solid #E0E0E0',
  borderRadius: 8,
  width: 30,
  height: 30
};

const topActionBtnStyle: CSSProperties = {
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 600,
  minHeight: 28,
  padding: '2px 12px',
  borderColor: '#E0E0E0',
  color: '#5D7083'
};

export interface MatchedProduct extends Partial<Product> {
  lastModifiedDate?: string;
  isStockProduct?: boolean;
  external?: boolean;
}

export interface OtherMatchesProps {
  _id: string;
  id: string;
  reasoning?: string;
  percentage?: number;
  isPrivateLabel?: boolean;
  isPreferredVendor?: boolean;
  order: number;
}

export interface ProductCardProps extends RFQProducts {
  totalMatched: number;
  selected?: boolean;
  matched: MatchedProduct;
  search: string;
  index: number;
  order?: number;
  hasPrivateLabels?: boolean;
  internalProducts?: OtherMatchesProps[];
  actionButton?: JSX.Element;
  expandedView?: boolean;
  // While true, the matched product cell shows a loading overlay (a match swap
  // is saving and we're awaiting the update endpoint).
  isSwapping?: boolean;
  // Other members currently focused on this row — rendered as the inline
  // avatar + name presence indicator left of the notes button (DEV-412).
  presenceMembers?: SpaceMember[];

  // Alternate Items (QOT-1580). Only rendered when the double gate is on
  // (tenant flag + per-RFQ flag), passed from the content layer.
  alternatesEnabled?: boolean;
  onReorder?: (from: RfqPosition, to: RfqPosition) => void;
  onRemoveAlt?: (slot: RfqAlternateSlot) => void;
  onResetPosition?: (position: RfqPosition) => void;
  onReplacePosition?: (position: RfqPosition) => void;
  // Opens the product details modal for a given alternate's product (mirrors
  // openProductDetails, which always opens the Top match). QOT-1732.
  openAlternateDetails?: (alternate: RfqAlternate) => void;

  onSelect?: (checked: boolean) => void;
  onIgnore: () => void;
  showMoreProducts: (i: number) => void;
  handleManualEntry: (props: ManualProps, index: number) => void;
  openProductDetails: () => void;
  handleProductChange: (
    product: Product,
    index: number,
    isExternal?: boolean
  ) => void;
}

const ProductCard = ({
  manufacturerSku,
  mfgSkuMatched,
  foundOnline,
  actionButton,
  hasPrivateLabels,
  internalProducts,
  totalMatched,
  manualEntry,
  foundItem,
  selected,
  ignored,
  matched,
  search,
  index,
  order,
  sell,
  expandedView = true,
  presenceMembers,
  isSwapping,
  alternatesEnabled,
  onReorder,
  onRemoveAlt,
  onResetPosition,
  onReplacePosition,
  openAlternateDetails,
  handleProductChange,
  openProductDetails,
  handleManualEntry,
  showMoreProducts,
  onSelect,
  ...rest
}: ProductCardProps) => {
  const id = useRfqId();

  const { openDialog, closeDialog } = useDialog();
  const { captureAnalytics } = useAnalytics();
  const { palette } = useTheme();
  const {
    broadcastItemUpdate,
    broadcastNoteAdded,
    broadcastNoteUpdated,
    broadcastNoteDeleted,
    broadcastCounterChanged
  } = useRfqRealtimeBroadcast();

  const [isLocallyExpanded, setIsLocallyExpanded] = useState(false);
  const effectiveExpanded = expandedView || isLocallyExpanded;

  const rfqItemId = rest?.id as string | undefined;
  const customerNote = (rest as any)?.customerNote as string | undefined;
  const hasNotes =
    !!customerNote?.trim() || (rest?.internalNotesCount || 0) > 0;

  const { mutateAsync: saveCustomerNote } = useSubscribedMutation(
    saveCustomerNoteMutation(),
    [AutoRFQQueryKey.GET_ITEMS]
  );
  const { mutateAsync: deleteCustomerNote } = useSubscribedMutation(
    deleteCustomerNoteMutation(),
    [AutoRFQQueryKey.GET_ITEMS]
  );
  const { mutateAsync: createInternalNote } = useSubscribedMutation(
    createInternalNoteMutation(),
    [AutoRFQQueryKey.GET_INTERNAL_NOTES]
  );
  const { mutateAsync: updateInternalNote } = useSubscribedMutation(
    updateInternalNoteMutation(),
    [AutoRFQQueryKey.GET_INTERNAL_NOTES]
  );
  const { mutateAsync: deleteInternalNote } = useSubscribedMutation(
    deleteInternalNoteMutation(),
    [AutoRFQQueryKey.GET_INTERNAL_NOTES]
  );
  const isNoMatch =
    !matched || rest?.matchOverride === RfqMatchOverride.MarkedAsNonMatched;

  const { competitor, quoteId, updateProcessedProduct } = useRFQStore();
  const { pricing, rfqToQuoteEnabled } = useRfqConfigStore();
  const { configuration } = useCatalogConfig();

  // Open the Customer/Internal notes as a centered modal (via the shared dialog
  // provider) rather than a popover anchored to the item's notes button.
  const openNotes = () => {
    openDialog(
      <NotesDialog
        rfqItemId={rfqItemId}
        customerNote={customerNote}
        productName={
          toTitleCase(matched?.name || '') || rest?.productName || rest?.desc
        }
        onClose={() => closeDialog(NOTES_DIALOG)}
        onSaveCustomer={async note => {
          if (!rfqItemId) return;
          const response: any = await saveCustomerNote({ rfqItemId, note });
          const nextNote =
            response?.Data?.customerNote ??
            response?.data?.customerNote ??
            response?.customerNote ??
            note;
          updateProcessedProduct(rfqItemId, { customerNote: nextNote });
          // Customer note rides the item document — send just the changed
          // field so collaborators merge it instantly (no stale refetch).
          broadcastItemUpdate(rfqItemId, { customerNote: nextNote });
        }}
        onDeleteCustomer={async () => {
          if (!rfqItemId) return;
          await deleteCustomerNote({ rfqItemId });
          updateProcessedProduct(rfqItemId, { customerNote: '' });
          broadcastItemUpdate(rfqItemId, { customerNote: '' });
        }}
        onSaveInternal={async content => {
          if (!rfqItemId) return;
          await createInternalNote({ rfqItemId, content });
          updateProcessedProduct(rfqItemId, {
            internalNotesCount: (rest?.internalNotesCount || 0) + 1
          });
          broadcastNoteAdded(rfqItemId);
          broadcastCounterChanged(rfqItemId, 'internalNotesCount', 1);
        }}
        onEditInternal={async (n, content) => {
          if (!rfqItemId) return;
          await updateInternalNote({
            rfqItemId,
            noteId: n._id,
            content
          });
          broadcastNoteUpdated(rfqItemId);
        }}
        onDeleteInternal={async n => {
          if (!rfqItemId) return;
          await deleteInternalNote({ rfqItemId, noteId: n._id });
          updateProcessedProduct(rfqItemId, {
            internalNotesCount: Math.max((rest?.internalNotesCount || 0) - 1, 0)
          });
          broadcastNoteDeleted(rfqItemId, n._id);
          broadcastCounterChanged(rfqItemId, 'internalNotesCount', -1);
        }}
      />,
      { id: NOTES_DIALOG }
    );
  };

  const notesButton = (
    <Tooltip arrow title="Notes">
      <IconButton
        size="small"
        onClick={() => openNotes()}
        style={{ position: 'relative' }}>
        <DescriptionOutlinedIcon htmlColor="#5D7083" style={{ fontSize: 20 }} />
        {hasNotes && (
          <Box
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#FA2020'
            }}
          />
        )}
      </IconButton>
    </Tooltip>
  );

  // Shown next to the note button while a match swap is saving (until the
  // update endpoint responds). No card overlay — just an inline spinner.
  const swapLoader = isSwapping ? (
    <Tooltip arrow title="Saving match…">
      <Box
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <CircularProgress size={18} />
      </Box>
    </Tooltip>
  ) : null;

  const showStock = configuration?.product?.grid?.stock?.show || false;

  const source = useMemo(
    () => (matched?.stockInfo ? 'catalog' : 'manuall'),
    [matched]
  );
  const isFoundOnlineSelected = useMemo(
    () => foundItem?.name === matched?.name,
    [foundItem?.name, matched?.name]
  );

  const titleReasoning = useMemo(() => {
    if (source === 'catalog') return 'Product was added from catalog.';
    if (isFoundOnlineSelected)
      return 'Product was imported from an online source.';

    return 'Product was added manually.';
  }, [source, isFoundOnlineSelected]);

  // Whether the Top is an off-list pick (Manual Entry / Manual Selection) that
  // should carry the ExternalChip. This used to gate on `matched.external`, but
  // the positions endpoint doesn't persist `externalSource`, so after a refresh
  // `external` came back false and the chip vanished. Derive it from the same
  // signal the "added manually / from catalog" reasoning already uses (no AI %
  // and not an online import) so the chip survives reload; the label itself
  // (Manual Entry vs Manual Selection) still comes from `stockInfo`.
  const isManualPick = useMemo(
    () => !!matched && !matched?.percentage && !isFoundOnlineSelected,
    [matched, isFoundOnlineSelected]
  );

  const src = useMemo(() => {
    const image =
      matched?.imageUrl ||
      matched?.images?.[0]?.path ||
      '/images/product-placeholder.svg';

    if (isFoundOnlineSelected) return '/images/empty/product-found-online.png';

    return image;
  }, [matched, isFoundOnlineSelected]);

  const privateLabelMatched = internalProducts?.filter(
    ({ isPrivateLabel }) => isPrivateLabel
  );

  // Find All privateLabelItems
  const privateLabelItems = useMemo(() => {
    const filteredApItems = privateLabelMatched?.filter(
      pl => pl?.id !== matched?._id && pl?._id !== matched?._id
    );
    const filteredProcessedItems = rest?.matchedInternalProducts?.filter(
      pl => pl?._id !== matched?._id && (pl?.isPrivateLabel || pl?.privateLabel)
    );

    const currItems = privateLabelMatched?.length
      ? filteredApItems
      : filteredProcessedItems;

    return currItems || [];
  }, [privateLabelMatched, rest?.matchedInternalProducts, matched]);

  // Showe Private Label button if there are other options
  const showPrivateLabels = useMemo(() => {
    const totalLabels = privateLabelItems?.length;
    if (!totalLabels) return false;

    return hasPrivateLabels;
  }, [hasPrivateLabels, privateLabelItems]);

  const itemRemovedFromQuotes = useMemo(
    () => quoteId && !rest?.inQuote,
    [quoteId, rest?.inQuote]
  );

  // --- Alternate Items (QOT-1580) ---
  const alternates = (rest?.alternates || [])
    .slice()
    .sort((a, b) => a.slot - b.slot);
  const slot1 = alternates.find(a => a.slot === 1);
  const slot2 = alternates.find(a => a.slot === 2);
  const alt1Filled = slot1?.state === 'filled';
  const alt2Filled = slot2?.state === 'filled';
  const filledAltCount = alternates.filter(a => a.state === 'filled').length;
  const showAlternates = !!alternatesEnabled && alternates.length > 0;
  // Show the Top "reset to AI match" only when Top diverged from the AI pick.
  const topDivergedFromAi =
    !!rest?.topAiOriginal &&
    !!matched?._id &&
    matched?._id !== rest?.topAiOriginal?.productId;

  const renderAlternatesBlock = () => (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
      {[slot1, slot2].map((alt, i) => {
        if (!alt) return null;
        const slotLabel = (i + 1) as 1 | 2;
        const position: RfqPosition = i === 0 ? 'alt1' : 'alt2';
        // A filled slot can always move up — there's always a position above
        // (Top for alt1, alt1 for alt2). When the slot above is empty the move
        // simply promotes this alternate into it.
        const canMoveUp = i === 0 ? true : alt2Filled;
        const canMoveDown = i === 0 ? alt1Filled : false;
        return (
          <AlternateSlot
            key={alt.slot}
            alternate={alt}
            slotLabel={slotLabel}
            search={search}
            rfqToQuoteEnabled={rfqToQuoteEnabled}
            pricingMode={pricing?.mode}
            defaultMarginEnabled={pricing?.defaultMarginEnabled}
            showStock={showStock}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
            onMoveUp={() => onReorder?.(position, i === 0 ? 'top' : 'alt1')}
            onMoveDown={() => onReorder?.(position, 'alt2')}
            onReplace={() => onReplacePosition?.(position)}
            onRemove={() => onRemoveAlt?.(alt.slot)}
            onReset={() => onResetPosition?.(position)}
            onProductClick={() => openAlternateDetails?.(alt)}
            onMore={() => {
              // "More alternatives" edits THIS alternate's slot (not the Top).
              // Opening its own position makes the modal preselect this
              // alternate's product + vendor-type filter and write back to the
              // correct slot on select.
              onReplacePosition?.(position);

              captureAnalytics(
                {
                  action: 'matches',
                  id: matched?._id,
                  total: totalMatched,
                  rfqId: id,
                  ...(!!competitor && {
                    competitorId: competitor?._id || ''
                  })
                },
                AnalyticsModules.AutoRFQItem
              );
            }}
          />
        );
      })}
    </Box>
  );

  // Preview of the alternatives inside the chip — one per line ("Alt 1: <name>
  // (SKU <sku>)") so the rep can see what's contained without expanding the
  // line (QOT-1736). Only filled slots are listed, in slot order.
  const alternatesTooltip = (
    <Box
      style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 2 }}>
      {alternates
        .filter(a => a.state === 'filled')
        .map(a => (
          <span key={a.slot}>
            {`Alt ${a.slot}: ${toTitleCase(a.name || '')}${
              a.sku ? ` (SKU ${a.sku})` : ''
            }`}
          </span>
        ))}
    </Box>
  );

  const alternatesChip =
    showAlternates && filledAltCount > 0 ? (
      <Tooltip arrow placement="top" title={alternatesTooltip}>
        <Box
          component="button"
          onClick={() => {
            alternatesAnalytics.chipOpen(rest?.id);
            setIsLocallyExpanded(true);
          }}
          style={{
            border: 'none',
            cursor: 'pointer',
            background: '#E8F3FF',
            color: '#1E96FC',
            textAlign: 'center',
            fontFamily: 'Roboto',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '16px',
            letterSpacing: 0,
            borderRadius: 999,
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 4,
            padding: '4px 8px',
            width: 'fit-content'
          }}>
          {`+${filledAltCount} Alternative${filledAltCount > 1 ? 's' : ''}`}
        </Box>
      </Tooltip>
    ) : null;

  const tirggerPrivateLabels = () => {
    // Capture Analytics
    captureAnalytics(
      {
        action: 'view',
        subAction:
          matched?.isPrivateLabel || matched?.privateLabel
            ? 'Additional Private Labels Found'
            : 'Private Label Found',
        id: matched?.sku || ''
      },
      AnalyticsModules.AutoRFQDetails
    );

    const internalProductsPrivateLabels: any = internalProducts?.filter(
      ({ isPrivateLabel }: { isPrivateLabel?: boolean }) => isPrivateLabel
    );

    openDialog(
      <PrivateLabels
        fetch
        ids={internalProductsPrivateLabels?.map(
          ({ _id }: { _id: string }) => _id
        )}
        competitorName={rest?.productName || rest?.desc}
        competitorSku={rest?.competitorSku}
        products={internalProductsPrivateLabels}
        onClose={() => closeDialog('private-labels')}
        selected={{ ...matched, _id: matched?._id || '' } as Product}
        setSelected={(p: Product) => {
          handleProductChange(p, index - 1, false);
          closeDialog('private-labels');
        }}
      />,
      {
        id: 'private-labels'
      }
    );
  };

  return (
    <Container container alignItems="stretch">
      <Competitor
        manufacturerSku={manufacturerSku}
        ignored={ignored}
        search={search}
        order={order}
        matched={matched}
        expandedView={effectiveExpanded}
        handleManualEntry={handleManualEntry}
        {...rest}
      />

      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
        <SETVIImage src="/icons/exchange.svg" alt="Exchange" />
      </Box>

      <Grid
        item
        xs
        style={{
          height: '100%'
        }}>
        {isNoMatch && (
          <NoMatch
            handleProductChange={handleProductChange}
            handleManualEntry={handleManualEntry}
            index={index}
            expandedView={effectiveExpanded}
            presenceMembers={presenceMembers}
            onExpand={
              !expandedView ? () => setIsLocallyExpanded(true) : undefined
            }
            onCollapse={
              !expandedView && isLocallyExpanded
                ? () => setIsLocallyExpanded(false)
                : undefined
            }
            {...rest}
          />
        )}
        {!isNoMatch &&
          (!effectiveExpanded ? (
            <FlexRow compact>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  width: '100%'
                }}>
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 12,
                    alignItems: 'center',
                    width: '100%'
                  }}>
                  <ImagePreview
                    title={
                      <img
                        src={src}
                        alt="product-img"
                        onError={e => {
                          e.currentTarget.src =
                            '/images/product-placeholder.svg';
                        }}
                        style={{
                          width: 180,
                          height: 180,
                          objectFit: 'contain',
                          backgroundColor: palette.common.white,
                          borderRadius: 10,
                          padding: 20
                        }}
                      />
                    }
                    placement="right"
                    arrow>
                    <Box
                      style={{
                        width: 45,
                        height: 45,
                        minWidth: 45,
                        minHeight: 45,
                        maxWidth: 45,
                        maxHeight: 45,
                        borderRadius: 10,
                        border: '1px solid #E0E0E0',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundColor: '#fff'
                      }}>
                      <img
                        src={src}
                        alt={matched?.name || 'Product'}
                        onError={e => {
                          e.currentTarget.src =
                            '/images/product-placeholder.svg';
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    </Box>
                  </ImagePreview>

                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      gap: 2,
                      minWidth: 0
                    }}>
                    <SHighlightText
                      value={toTitleCase(matched?.name || '')}
                      fontColor="#333D47"
                      searchQuery={search}
                      onClick={() => {
                        if (matched?.external && !matched?.lastModifiedDate)
                          return;
                        if (matched?._id) openProductDetails?.();
                      }}
                      maxLines={1}
                      weight="semibold"
                      style={{
                        cursor:
                          matched?.external && !matched?.lastModifiedDate
                            ? 'inherit'
                            : 'pointer'
                      }}
                    />

                    <Box display="flex" alignItems="center" gridGap={8}>
                      <SHighlightText
                        value={`SKU: ${matched?.sku ? `${matched?.sku}` : '-'}`}
                        searchQuery={search}
                        size="sm"
                        maxLines={1}
                        fontColor="#5D7083"
                      />
                      <Box component="span" style={{ color: '#5D7083' }}>
                        •
                      </Box>
                      <SHighlightText
                        value={`MFG: ${toTitleCase(
                          matched?.vendor?.name || ''
                        )} ${
                          matched?.manufacturerSku
                            ? `(${matched?.manufacturerSku})`
                            : ''
                        }`}
                        searchQuery={search}
                        size="sm"
                        maxLines={1}
                        fontColor="#5D7083"
                      />
                    </Box>
                  </Box>

                  <Box
                    display="flex"
                    alignItems="center"
                    gridGap={20}
                    style={{ minWidth: 'fit-content' }}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-end"
                      gridGap={6}>
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <PricingDisplay
                          cost={matched?.cost}
                          sell={sell}
                          search={search}
                          rfqToQuoteEnabled={rfqToQuoteEnabled}
                          pricingMode={pricing?.mode}
                          isManual={isManualPick}
                          showEndDot={
                            !isFoundOnlineSelected && !manualEntry && showStock
                          }
                          defaultMarginEnabled={pricing?.defaultMarginEnabled}
                        />
                        {!isFoundOnlineSelected &&
                          !manualEntry &&
                          showStock && (
                            <Stock
                              newDesign
                              inventory={matched?.inventory ?? null}
                              isDropship={!!matched?.dropship}
                              isStockProduct={matched?.isStockProduct}
                            />
                          )}
                      </Box>

                      <Box display="flex" alignItems="center" gridGap={8}>
                        <SButton
                          variant="text"
                          size="small"
                          onClick={() => {
                            showMoreProducts(index - 1);

                            captureAnalytics(
                              {
                                action: 'matches',
                                id: matched?._id,
                                total: totalMatched,
                                rfqId: id,
                                ...(!!competitor && {
                                  competitorId: competitor?._id || ''
                                })
                              },
                              AnalyticsModules.AutoRFQItem
                            );
                          }}
                          style={{
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            padding: '0 4px',
                            minWidth: 0,
                            minHeight: 0,
                            textTransform: 'none',
                            background: 'transparent'
                          }}>
                          View More Matches
                        </SButton>
                        {isManualPick && (
                          <ExternalChip stockInfo={matched?.stockInfo} />
                        )}
                        {itemRemovedFromQuotes && (
                          <Tooltip
                            title="This Item is removed from Quote"
                            arrow>
                            <RemovedBadge
                              style={{ width: 24, height: 24, minWidth: 24 }}>
                              <SlashedFileIconV2
                                fill="#FA2020"
                                style={{ width: 12, height: 12 }}
                              />
                            </RemovedBadge>
                          </Tooltip>
                        )}
                        {!!matched?.percentage && (
                          <MatchIndicator
                            percentage={matched?.percentage}
                            compact
                          />
                        )}
                        {!!matched?.percentage && (
                          <Tooltip
                            arrow
                            title={matched?.reasoning || ''}
                            disableHoverListener={!matched?.reasoning}>
                            <Box>
                              <AIProductMatchBadge
                                value={matched?.percentage}
                                fontSize={12}
                              />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    <Box
                      display="flex"
                      alignItems="center"
                      gridGap={18}
                      style={{ alignSelf: 'center' }}>
                      <RowPresence members={presenceMembers} />
                      {swapLoader}
                      {notesButton}
                      {!!actionButton && (
                        <Tooltip arrow title="More actions">
                          <span style={{ display: 'inline-flex' }}>
                            {actionButton}
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip arrow title="Expand">
                        <IconButton
                          size="small"
                          onClick={() => setIsLocallyExpanded(true)}>
                          <ExpandMoreRoundedIcon
                            htmlColor="#5D7083"
                            style={{ fontSize: 20 }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>

                {alternatesChip && (
                  <Box style={{ marginLeft: 57 }}>{alternatesChip}</Box>
                )}
              </Box>
            </FlexRow>
          ) : (
            <FlexRow>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 12,
                  width: '100%'
                }}>
                <ImagePreview
                  title={
                    <img
                      src={src}
                      alt="product-img"
                      onError={e => {
                        e.currentTarget.src = '/images/product-placeholder.svg';
                      }}
                      style={{
                        width: 180,
                        height: 180,
                        objectFit: 'contain',
                        backgroundColor: palette.common.white,
                        borderRadius: 10,
                        padding: 20
                      }}
                    />
                  }
                  placement="right"
                  arrow>
                  <Box
                    minWidth={110}
                    width={110}
                    minHeight={85}
                    display="flex"
                    alignItems="center"
                    justifyContent="center">
                    <SETVIImage
                      src={src}
                      alt={matched?.name || 'Product'}
                      width={85}
                      height={85}
                      radius={12}
                      fit="contain"
                      style={{
                        maxHeight: 85,
                        maxWidth: 85,
                        border: '1px solid #E0E0E0'
                      }}
                    />
                  </Box>
                </ImagePreview>

                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    flex: 1
                  }}>
                  <Box
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 6
                    }}>
                    <Box
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 6
                      }}>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6
                        }}>
                        <SHighlightText
                          value={toTitleCase(matched?.name || '')}
                          fontColor="#333D47"
                          searchQuery={search}
                          onClick={() => {
                            if (matched?.external && !matched?.lastModifiedDate)
                              return;
                            if (matched?._id) openProductDetails?.();
                          }}
                          maxLines={1}
                          weight="semibold"
                          style={{
                            cursor:
                              matched?.external && !matched?.lastModifiedDate
                                ? 'inherit'
                                : 'pointer'
                          }}
                        />

                        {(matched?.privateLabel || matched?.isPrivateLabel) && (
                          <ProductLabel />
                        )}

                        {(matched?.vendorHierarchy > 0 ||
                          matched?.vendorRanking > 0 ||
                          matched?.isPreferredVendor) && (
                          <ProductLabel type="strategic-vendor" />
                        )}
                      </Box>
                    </Box>

                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 12
                      }}>
                      {/* Show not in quotes indicator */}
                      {itemRemovedFromQuotes && (
                        <Tooltip title="This Item is removed from Quote" arrow>
                          <RemovedBadge>
                            <SlashedFileIconV2
                              fill="#FA2020"
                              style={{
                                width: 14,
                                height: 14
                              }}
                            />
                          </RemovedBadge>
                        </Tooltip>
                      )}
                      <RowPresence members={presenceMembers} />
                      {swapLoader}
                      {notesButton}
                      {!!actionButton && (
                        <Tooltip arrow title="More actions">
                          <span style={{ display: 'inline-flex' }}>
                            {actionButton}
                          </span>
                        </Tooltip>
                      )}
                      {!expandedView && isLocallyExpanded && (
                        <Tooltip arrow title="Collapse">
                          <IconButton
                            size="small"
                            onClick={() => setIsLocallyExpanded(false)}>
                            <ExpandMoreRoundedIcon
                              htmlColor="#5D7083"
                              style={{
                                fontSize: 20,
                                transform: 'rotate(180deg)'
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 4,
                      alignItems: 'center',
                      color: '#5D7083'
                    }}>
                    <SHighlightText
                      value={`SKU: ${matched?.sku ? `${matched?.sku}` : '-'}`}
                      searchQuery={search}
                      size="sm"
                      maxLines={1}
                      fontColor="#5D7083"
                    />
                    •
                    <SHighlightText
                      value={`MFG: ${toTitleCase(
                        matched?.vendor?.name || ''
                      )} ${
                        matched?.manufacturerSku
                          ? `(${matched?.manufacturerSku})`
                          : ''
                      }`}
                      searchQuery={search}
                      size="sm"
                      maxLines={1}
                      fontColor="#5D7083"
                    />
                    {!!matched?.active && (
                      <ProductStatusBadge
                        style={{
                          minWidth: 'fit-content'
                        }}>
                        <SText title={getProductStatus(matched as Product)} />
                      </ProductStatusBadge>
                    )}
                    {!!matched?.stockInfo && (
                      <ProductStatusBadge>
                        <SText title={getStockInfo(matched?.stockInfo)} />
                      </ProductStatusBadge>
                    )}
                  </Box>

                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 4,
                      alignItems: 'center',
                      color: '#5D7083'
                    }}>
                    <PricingDisplay
                      cost={matched?.cost}
                      sell={sell}
                      search={search}
                      rfqToQuoteEnabled={rfqToQuoteEnabled}
                      pricingMode={pricing?.mode}
                      isManual={isManualPick}
                      showEndDot={
                        !isFoundOnlineSelected && !manualEntry && showStock
                      }
                      defaultMarginEnabled={pricing?.defaultMarginEnabled}
                    />

                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                      {!isFoundOnlineSelected && !manualEntry && showStock && (
                        <Stock
                          newDesign
                          inventory={matched?.inventory}
                          isDropship={matched?.dropship}
                          isStockProduct={matched?.isStockProduct}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box>
                <AdditionalContent
                  percentage={matched?.percentage}
                  text={
                    !matched?.percentage ? titleReasoning : matched?.reasoning
                  }
                  withBottomBorder
                  prefix={matched?.percentage && 'AI Reasoning:'}
                  customChip={
                    isManualPick && (
                      <ExternalChip stockInfo={matched?.stockInfo} />
                    )
                  }
                />
              </Box>

              {/* View More Matches (left) shares one row with the action
                  buttons (Found Online / Private Label) and top-position
                  controls on the right, above the alternatives list. */}
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                <SButton
                  variant="text"
                  size="small"
                  onClick={() => {
                    showMoreProducts(index - 1);

                    captureAnalytics(
                      {
                        action: 'matches',
                        id: matched?._id,
                        total: totalMatched,
                        rfqId: id,
                        ...(!!competitor && {
                          competitorId: competitor?._id || ''
                        })
                      },
                      AnalyticsModules.AutoRFQItem
                    );
                  }}
                  style={iconStyle}>
                  View More Matches
                </SButton>

                <Box display="flex" alignItems="center" gridGap={12}>
                  {!!foundOnline && manufacturerSku && rest?.manufacturer && (
                    <SButton
                      variant="text"
                      size="small"
                      disabled={!foundItem}
                      onClick={() => {
                        openDialog(
                          <BaseDialog
                            title="Product Found Online"
                            onClose={closeDialog}
                            elements={[
                              {
                                content: (
                                  <ManualEntry
                                    product={matched as Product}
                                    values={{
                                      url: foundItem?.url,
                                      existinInDb:
                                        foundItem?.isManufacturerExistInDB,
                                      sku: foundItem?.manufacturerSku,
                                      name: foundItem?.name,
                                      price: '',
                                      manufacturer: foundItem?.manufacturer
                                    }}
                                    onClose={closeDialog}
                                    handleSubmit={props => {
                                      handleManualEntry(props, index - 1);
                                    }}
                                  />
                                ),
                                name: 'Manual Entry',
                                key: Tabs.MANUAL
                              }
                            ]}
                          />
                        );

                        captureAnalytics(
                          {
                            action: 'view',
                            subAction: 'Found Online',
                            id: matched?.sku || ''
                          },
                          AnalyticsModules.AutoRFQDetails
                        );
                      }}
                      style={{
                        ...iconStyle
                      }}>
                      Found Online
                    </SButton>
                  )}

                  {/* With Additional Options ON, Private Label is surfaced as an
                      alternative (+ the Private Label filter in the unified
                      "View More Matches" modal), so this link is redundant —
                      hide it. Gated on the feature flag (not the per-line
                      showAlternates) so it's removed whenever ON. When OFF it
                      stays exactly as before (QOT-1747). */}
                  {showPrivateLabels && !alternatesEnabled && (
                    <AnchorBtn variant="text" onClick={tirggerPrivateLabels}>
                      {matched?.isPrivateLabel || matched?.privateLabel
                        ? 'Additional Private Labels Found'
                        : 'Private Label Found'}
                    </AnchorBtn>
                  )}

                  {showAlternates && (
                    <Box display="flex" alignItems="center" gridGap={8}>
                      {/* Top joins the reorder stack: ↑ is disabled (it's already
                          first), ↓ demotes it into Alternative 1. Reorder icons +
                          labeled Reset match the alternate rows (QOT-1748/1751). */}
                      <Box display="flex" alignItems="center" gridGap={4}>
                        <Tooltip arrow title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              disabled
                              style={{ ...reorderBtnStyle, opacity: 0.4 }}>
                              <KeyboardArrowUpRoundedIcon
                                style={{ fontSize: 18 }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip arrow title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!alt1Filled}
                              style={{
                                ...reorderBtnStyle,
                                opacity: alt1Filled ? 1 : 0.4
                              }}
                              onClick={() => onReorder?.('top', 'alt1')}>
                              <KeyboardArrowDownRoundedIcon
                                style={{ fontSize: 18 }}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      {topDivergedFromAi && (
                        <>
                          <Divider
                            orientation="vertical"
                            flexItem
                            style={{ margin: '2px 0' }}
                          />
                          <SButton
                            size="small"
                            variant="outlined"
                            color="default"
                            onClick={() => onResetPosition?.('top')}
                            startIcon={
                              <RestoreRoundedIcon style={{ fontSize: 16 }} />
                            }
                            style={topActionBtnStyle}>
                            Reset
                          </SButton>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              {showAlternates && renderAlternatesBlock()}
            </FlexRow>
          ))}
      </Grid>
    </Container>
  );
};

export default memo(ProductCard);
